/**
 * Vânt 3D animat — particule advectate în volumul ECMWF pe nivele de presiune.
 *
 * Advecția rulează pe CPU (câteva mii de particule, trilinear în u/v/w), dar
 * randarea se face printr-un singur `DrawCommand` Cesium peste un buffer de
 * segmente reîncărcat o dată pe cadru. Asta dă fluiditatea animației 2D fără
 * complexitatea unui sistem complet ping-pong pe GPU, și păstrează mișcarea
 * cu adevărat tridimensională: particulele urcă și coboară după `wind_w`.
 *
 * Obiectul se adaugă direct în `scene.primitives` — implementează contractul
 * de primitivă Cesium (`update`, `isDestroyed`, `destroy`).
 */
import {
  BlendingState, BoundingSphere, Buffer, BufferUsage, Cartesian3,
  ComponentDatatype, DrawCommand, Matrix4, Pass, PrimitiveType, RenderState,
  ShaderProgram, VertexArray,
} from 'cesium';

import * as zarr from 'zarrita';

import { windColorRGBA } from './wind-palette.js';

const MAX_PARTICLES = 3000;
const TRAIL = 8;                       // poziții păstrate per particulă
const SEGMENTS = TRAIL - 1;
const VERTEX_COUNT = MAX_PARTICLES * SEGMENTS * 2;

// Vântul real e mult prea lent pentru scara hărții: la 10 m/s o particulă ar
// traversa România în ~22 de ore. Accelerăm ca traversarea să dureze ~10 s.
const SPEED_FACTOR = 9000;

/**
 * Câte cadre trec între două puncte fixate în urmă.
 *
 * Dacă am fixa un punct la fiecare cadru, cele opt poziții ar acoperi opt
 * cadre — sub un kilometru, adică mai puțin de un pixel la scara țării, și
 * particulele ar apărea ca puncte. Fixând mai rar, dâra se întinde pe câteva
 * zeci de kilometri fără să accelerăm mișcarea.
 */
const TRAIL_COMMIT_FRAMES = 8;

const MIN_AGE_S = 4;
const MAX_AGE_S = 11;

const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LON_EQ = 111320;

const VERTEX_SHADER = `
in vec3 position;
in vec4 color;
out vec4 v_color;
void main() {
  v_color = color;
  gl_Position = czm_modelViewProjection * vec4(position, 1.0);
  czm_vertexLogDepth();
}
`;

const FRAGMENT_SHADER = `
in vec4 v_color;
void main() {
  out_FragColor = v_color;
  czm_writeLogDepth();
}
`;

/** Interpolare biliniară într-un nivel al cubului. */
function bilinear(data, nlat, nlon, levOffset, fy, fx) {
  const y0 = Math.max(0, Math.min(nlat - 1, Math.floor(fy)));
  const x0 = Math.max(0, Math.min(nlon - 1, Math.floor(fx)));
  const y1 = Math.min(nlat - 1, y0 + 1);
  const x1 = Math.min(nlon - 1, x0 + 1);
  const ty = fy - y0, tx = fx - x0;

  const v00 = data[levOffset + y0 * nlon + x0];
  const v01 = data[levOffset + y0 * nlon + x1];
  const v10 = data[levOffset + y1 * nlon + x0];
  const v11 = data[levOffset + y1 * nlon + x1];

  const a = v00 + (v01 - v00) * tx;
  const b = v10 + (v11 - v10) * tx;
  return a + (b - a) * ty;
}

export class Wind3DField {
  /**
   * @param {import('cesium').Viewer} viewer
   * @param {object} opts
   * @param {string} opts.url  URL-ul store-ului Zarr cu nivele de presiune
   */
  constructor(viewer, { url }) {
    this.viewer = viewer;
    this.url = url;

    this.available = false;
    this.enabled = false;
    this.exaggeration = 2;
    this.timeIndex = 0;

    // Reglabile din interfață
    this.count = MAX_PARTICLES;        // câte particule sunt simulate și desenate
    this.speedFactor = SPEED_FACTOR;   // cât de repede curge câmpul pe ecran
    this.trailStretch = TRAIL_COMMIT_FRAMES;  // cadre între două puncte fixate
    this.opacity = 1;                  // ca vântul să nu acopere secțiunile

    // Sursă scalară opțională: când e setată, particulele devin trasori —
    // se seamănă acolo unde câmpul e mare și se colorează după el, în loc de
    // după viteza vântului. Vântul rămâne cel care le transportă.
    this.scalar = null;

    this.coords = null;
    this._field = null;          // { u, v, w, shape, meanAlt }
    this._arrays = {};
    this._cache = new Map();

    // Starea particulelor
    this._lon = new Float64Array(MAX_PARTICLES);
    this._lat = new Float64Array(MAX_PARTICLES);
    this._alt = new Float64Array(MAX_PARTICLES);
    this._age = new Float32Array(MAX_PARTICLES);
    this._life = new Float32Array(MAX_PARTICLES);
    this._speed = new Float32Array(MAX_PARTICLES);
    this._value = new Float32Array(MAX_PARTICLES);  // valoarea scalară la particulă

    // Urme: inel de TRAIL poziții, în ECEF, cu un cap comun tuturor
    this._trail = new Float64Array(MAX_PARTICLES * TRAIL * 3);
    this._head = 0;
    this._frame = 0;

    // Buffere de desenare
    this._positions = new Float32Array(VERTEX_COUNT * 3);
    this._colors = new Uint8Array(VERTEX_COUNT * 4);

    this._center = Cartesian3.ZERO;
    this._scratch = new Cartesian3();
    this._lastMs = 0;
    this._inScene = false;
    this._destroyed = false;

    // Resurse GL, create la primul cadru
    this._va = null;
    this._sp = null;
    this._rs = null;
    this._command = null;
    this._posBuffer = null;
    this._colBuffer = null;
  }

  // ── Date ────────────────────────────────────────────────────────
  /** Deschide store-ul și citește coordonatele. */
  async load() {
    const store = new zarr.FetchStore(this.url);
    const root = await zarr.open(store, { kind: 'group' });
    this._root = root;

    const [latA, lonA, levA, timeA] = await Promise.all([
      zarr.open(root.resolve('latitude'), { kind: 'array' }),
      zarr.open(root.resolve('longitude'), { kind: 'array' }),
      zarr.open(root.resolve('level'), { kind: 'array' }),
      zarr.open(root.resolve('time'), { kind: 'array' }),
    ]);
    const [lat, lon, lev, time] = await Promise.all([
      zarr.get(latA), zarr.get(lonA), zarr.get(levA), zarr.get(timeA),
    ]);

    this.coords = {
      lats: Array.from(lat.data, Number),
      lons: Array.from(lon.data, Number),
      levels: Array.from(lev.data, Number),
      times: time.data,
      timeAttrs: timeA.attrs || {},
    };

    const { lats, lons } = this.coords;
    this._latStep = (lats[lats.length - 1] - lats[0]) / (lats.length - 1);
    this._lonStep = (lons[lons.length - 1] - lons[0]) / (lons.length - 1);

    // Origine locală: pozițiile se trimit relativ la ea, ca Float32 să aibă
    // precizie suficientă (ECEF absolut ar da tremurat de ordinul metrilor).
    this._center = Cartesian3.fromDegrees(
      (lons[0] + lons[lons.length - 1]) / 2,
      (lats[0] + lats[lats.length - 1]) / 2,
      5000,
    );

    // Sfera de încadrare nu e opțională: Cesium sortează comenzile translucide
    // după distanță și cere `boundingVolume` chiar și când culling-ul e oprit.
    // Fără ea, orice pas de picking arunca excepție cu vântul pornit.
    const corner = Cartesian3.fromDegrees(lons[0], lats[0], 30000);
    this._radius = Cartesian3.distance(this._center, corner) * 1.25;

    this._arrays.u = await zarr.open(root.resolve('wind_u'), { kind: 'array' });
    this._arrays.v = await zarr.open(root.resolve('wind_v'), { kind: 'array' });
    this._arrays.gh = await zarr.open(root.resolve('geoheight'), { kind: 'array' });
    try {
      this._arrays.w = await zarr.open(root.resolve('wind_w'), { kind: 'array' });
    } catch {
      // Fără componentă verticală particulele rămân pe nivelul lor.
      this._arrays.w = null;
    }

    this.available = true;
    return this.coords;
  }

  /**
   * Leagă un câmp scalar de particule, transformându-le în trasori.
   *
   * @param {null|object} src  { cube, lats, lons, altitudes, colorFn, vmin, vmax }
   */
  setScalarSource(src) {
    if (!src) {
      this.scalar = null;
      if (this._field) this._seedAll();
      return;
    }
    const { lats, lons, altitudes } = src;
    this.scalar = {
      ...src,
      latStep: (lats[lats.length - 1] - lats[0]) / (lats.length - 1),
      lonStep: (lons[lons.length - 1] - lons[0]) / (lons.length - 1),
      altMin: altitudes[0],
      altMax: altitudes[altitudes.length - 1],
    };
    if (this._field) this._seedAll();
  }

  /** Interpolează câmpul scalar la o poziție; NaN în afara domeniului. */
  _sampleScalar(lon, lat, alt) {
    const sc = this.scalar;
    if (!sc) return NaN;
    const [nlev, nlat, nlon] = sc.cube.shape;

    const fy = (lat - sc.lats[0]) / sc.latStep;
    const fx = (lon - sc.lons[0]) / sc.lonStep;
    if (fy < 0 || fy > nlat - 1 || fx < 0 || fx > nlon - 1) return NaN;
    if (alt < sc.altMin || alt > sc.altMax) return NaN;

    let l = 0;
    while (l < nlev - 2 && sc.altitudes[l + 1] < alt) l++;
    const a0 = sc.altitudes[l], a1 = sc.altitudes[l + 1];
    const f = a1 === a0 ? 0 : Math.min(Math.max((alt - a0) / (a1 - a0), 0), 1);

    const s0 = bilinear(sc.cube.data, nlat, nlon, l * nlat * nlon, fy, fx);
    const s1 = bilinear(sc.cube.data, nlat, nlon, (l + 1) * nlat * nlon, fy, fx);
    return s0 + (s1 - s0) * f;
  }

  /** Numărul de pași de timp din volumul de vânt. */
  get timeSteps() {
    return this.coords ? this.coords.times.length : 0;
  }

  get hasVertical() {
    return !!this._arrays.w;
  }

  /** Încarcă câmpul pentru un pas de timp și îl pregătește pentru advecție. */
  async setTime(ti) {
    if (!this.available) return;
    const clamped = Math.max(0, Math.min(ti, this.timeSteps - 1));
    this.timeIndex = clamped;

    if (this._cache.has(clamped)) {
      this._field = this._cache.get(clamped);
      return;
    }

    const sel = [clamped, null, null, null];
    const [u, v, gh] = await Promise.all([
      zarr.get(this._arrays.u, sel),
      zarr.get(this._arrays.v, sel),
      zarr.get(this._arrays.gh, sel),
    ]);
    const w = this._arrays.w ? await zarr.get(this._arrays.w, sel) : null;

    const [nlev, nlat, nlon] = u.shape;

    // Profil mediu de altitudine pe nivel. Variația spațială a înălțimii
    // geopotențiale la o presiune dată e de ordinul a 100 m — neglijabilă
    // pentru plasarea particulelor, dar ne scutește de o interpolare 4D.
    const meanAlt = new Float64Array(nlev);
    const perLevel = nlat * nlon;
    for (let l = 0; l < nlev; l++) {
      let sum = 0;
      const off = l * perLevel;
      for (let i = 0; i < perLevel; i++) sum += gh.data[off + i];
      meanAlt[l] = sum / perLevel;
    }

    const field = { u: u.data, v: v.data, w: w ? w.data : null, shape: u.shape, meanAlt };
    if (this._cache.size > 6) this._cache.delete(this._cache.keys().next().value);
    this._cache.set(clamped, field);
    this._field = field;
  }

  // ── Simulare ────────────────────────────────────────────────────
  /**
   * Interpolează vântul la o poziție. Întoarce false în afara domeniului.
   * @param {{u:number,v:number,w:number}} out
   */
  _sample(lon, lat, alt, out) {
    const { lats, lons } = this.coords;
    const { u, v, w, shape, meanAlt } = this._field;
    const [nlev, nlat, nlon] = shape;

    const fy = (lat - lats[0]) / this._latStep;
    const fx = (lon - lons[0]) / this._lonStep;
    if (fy < 0 || fy > nlat - 1 || fx < 0 || fx > nlon - 1) return false;
    // Pe verticală limităm în loc să respingem: cel mai de jos nivel de
    // presiune e pe la 159 m, iar poluantul stă și sub el — o particulă de
    // lângă sol n-are motiv să moară doar fiindcă grila nu coboară până acolo.
    const altC = Math.min(Math.max(alt, meanAlt[0]), meanAlt[nlev - 1]);

    let l = 0;
    while (l < nlev - 2 && meanAlt[l + 1] < altC) l++;
    const a0 = meanAlt[l], a1 = meanAlt[l + 1];
    const fl = a1 === a0 ? 0 : Math.min(Math.max((altC - a0) / (a1 - a0), 0), 1);

    const o0 = l * nlat * nlon;
    const o1 = (l + 1) * nlat * nlon;

    const u0 = bilinear(u, nlat, nlon, o0, fy, fx);
    const u1 = bilinear(u, nlat, nlon, o1, fy, fx);
    const v0 = bilinear(v, nlat, nlon, o0, fy, fx);
    const v1 = bilinear(v, nlat, nlon, o1, fy, fx);

    out.u = u0 + (u1 - u0) * fl;
    out.v = v0 + (v1 - v0) * fl;

    if (w) {
      const w0 = bilinear(w, nlat, nlon, o0, fy, fx);
      const w1 = bilinear(w, nlat, nlon, o1, fy, fx);
      out.w = w0 + (w1 - w0) * fl;
    } else {
      out.w = 0;
    }
    return true;
  }

  /**
   * Plasează o particulă în domeniu și îi resetează urma.
   *
   * În modul trasor, poziția nu e uniformă ci ponderată de câmpul scalar,
   * prin respingere: încercăm poziții la întâmplare și o acceptăm pe prima
   * unde valoarea normalizată depășește un prag aleator. Astfel densitatea
   * particulelor redă concentrația, nu doar culoarea lor.
   */
  _seed(i) {
    const { lats, lons } = this.coords;
    const { meanAlt, shape } = this._field;
    const nlev = shape[0];
    const sc = this.scalar;

    // Domeniul de semănare: al câmpului scalar dacă există, altfel al vântului
    const loAlt = sc ? Math.max(0, sc.altMin) : meanAlt[0];
    const hiAlt = sc ? Math.min(sc.altMax, meanAlt[nlev - 1]) : meanAlt[nlev - 1];

    let lon = 0, lat = 0, alt = 0, val = NaN;
    const tries = sc ? 24 : 1;
    for (let t = 0; t < tries; t++) {
      lon = lons[0] + Math.random() * (lons[lons.length - 1] - lons[0]);
      lat = lats[0] + Math.random() * (lats[lats.length - 1] - lats[0]);
      alt = loAlt + Math.random() * (hiAlt - loAlt);
      if (!sc) break;

      val = this._sampleScalar(lon, lat, alt);
      if (isNaN(val)) continue;
      const norm = (val - sc.vmin) / Math.max(1e-9, sc.vmax - sc.vmin);
      // Rădăcina atenuează contrastul: fără ea, un câmp cu vârfuri rare ar
      // lăsa aproape tot domeniul gol.
      if (Math.sqrt(Math.max(0, Math.min(1, norm))) > Math.random()) break;
    }

    this._lon[i] = lon;
    this._lat[i] = lat;
    this._alt[i] = alt;
    this._value[i] = isNaN(val) ? 0 : val;
    this._age[i] = 0;
    this._life[i] = MIN_AGE_S + Math.random() * (MAX_AGE_S - MIN_AGE_S);
    this._speed[i] = 0;

    // Urma se colapsează în punctul de pornire, altfel particula ar apărea
    // cu o dâră care traversează harta de la poziția ei anterioară.
    const ecef = this._toEcef(this._lon[i], this._lat[i], this._alt[i]);
    const base = i * TRAIL * 3;
    for (let t = 0; t < TRAIL; t++) {
      this._trail[base + t * 3] = ecef.x;
      this._trail[base + t * 3 + 1] = ecef.y;
      this._trail[base + t * 3 + 2] = ecef.z;
    }
  }

  _seedAll() {
    if (!this._field) return;
    for (let i = 0; i < this.count; i++) this._seed(i);
  }

  _toEcef(lon, lat, alt) {
    return Cartesian3.fromDegrees(lon, lat, alt * this.exaggeration, undefined, this._scratch);
  }

  _step(dt) {
    const out = { u: 0, v: 0, w: 0 };
    const factor = this.speedFactor * dt;

    // Capul urmei se rescrie în fiecare cadru, ca segmentul din față să
    // crească lin; doar din TRAIL_COMMIT_FRAMES în TRAIL_COMMIT_FRAMES îl
    // fixăm și trecem la următorul slot din inel.
    this._frame = (this._frame + 1) % this.trailStretch;
    if (this._frame === 0) {
      const prev = this._head;
      this._head = (this._head + 1) % TRAIL;
      // Noul slot pornește din punctul fixat, nu din poziția lui veche.
      for (let i = 0; i < this.count; i++) {
        const base = i * TRAIL * 3;
        const from = base + prev * 3;
        const to = base + this._head * 3;
        this._trail[to] = this._trail[from];
        this._trail[to + 1] = this._trail[from + 1];
        this._trail[to + 2] = this._trail[from + 2];
      }
    }
    const headOffset = this._head * 3;

    for (let i = 0; i < this.count; i++) {
      this._age[i] += dt;

      let alive = this._age[i] < this._life[i];
      if (alive) alive = this._sample(this._lon[i], this._lat[i], this._alt[i], out);

      if (!alive) {
        this._seed(i);
        continue;
      }

      const lat = this._lat[i];
      const mPerDegLon = M_PER_DEG_LON_EQ * Math.cos(lat * Math.PI / 180);
      this._lon[i] += out.u * factor / mPerDegLon;
      this._lat[i] += out.v * factor / M_PER_DEG_LAT;
      this._alt[i] += out.w * factor;
      this._speed[i] = Math.hypot(out.u, out.v);
      if (this.scalar) {
        const v = this._sampleScalar(this._lon[i], this._lat[i], this._alt[i]);
        this._value[i] = isNaN(v) ? this._value[i] : v;
      }

      const ecef = this._toEcef(this._lon[i], this._lat[i], this._alt[i]);
      const base = i * TRAIL * 3 + headOffset;
      this._trail[base] = ecef.x;
      this._trail[base + 1] = ecef.y;
      this._trail[base + 2] = ecef.z;
    }
  }

  /** Transformă inelul de urme în segmente de linii, relativ la origine. */
  _buildVertices() {
    const pos = this._positions;
    const col = this._colors;
    const cx = this._center.x, cy = this._center.y, cz = this._center.z;

    let p = 0, c = 0;
    const sc = this.scalar;
    for (let i = 0; i < this.count; i++) {
      let r, g, b, baseAlpha;
      if (sc) {
        const c = sc.colorFn(this._value[i]);
        r = c[0]; g = c[1]; b = c[2];
        // colorFn dă alfa în 0–255; particulele au nevoie de 0–1
        baseAlpha = c[3] / 255;
      } else {
        [r, g, b, baseAlpha] = windColorRGBA(this._speed[i]);
      }
      const trailBase = i * TRAIL * 3;

      for (let s = 0; s < SEGMENTS; s++) {
        const iNew = (this._head - s + TRAIL) % TRAIL;
        const iOld = (this._head - s - 1 + TRAIL) % TRAIL;

        // Coada se stinge spre capătul vechi.
        const fadeNew = 1 - s / SEGMENTS;
        const fadeOld = 1 - (s + 1) / SEGMENTS;

        for (const [idx, fade] of [[iNew, fadeNew], [iOld, fadeOld]]) {
          const t = trailBase + idx * 3;
          pos[p++] = this._trail[t] - cx;
          pos[p++] = this._trail[t + 1] - cy;
          pos[p++] = this._trail[t + 2] - cz;

          col[c++] = r;
          col[c++] = g;
          col[c++] = b;
          col[c++] = baseAlpha * fade * this.opacity * 255;
        }
      }
    }
  }

  // ── Ciclu de viață ──────────────────────────────────────────────
  enable() {
    if (!this.available || this.enabled) return;
    this.enabled = true;
    this._seedAll();
    this._lastMs = 0;

    if (!this._inScene) {
      this.viewer.scene.primitives.add(this);
      this._inScene = true;
    }
    // Animația are nevoie de cadre continue.
    this._prevRenderMode = this.viewer.scene.requestRenderMode;
    this.viewer.scene.requestRenderMode = false;
  }

  disable() {
    if (!this.enabled) return;
    this.enabled = false;
    this.viewer.scene.requestRenderMode = this._prevRenderMode ?? true;
    this.viewer.scene.requestRender();
  }

  /** Recalculează urmele când se schimbă scara verticală. */
  refreshExaggeration() {
    if (this.enabled && this._field) this._seedAll();
  }

  /**
   * Schimbă numărul de particule active.
   *
   * Bufferele rămân dimensionate la maxim; scade doar câte vârfuri desenăm.
   * Particulele nou activate n-au stare validă, deci le resemănăm pe toate.
   *
   * @param {number} fraction 0–1 din numărul maxim
   */
  setDensity(fraction) {
    const n = Math.max(50, Math.round(MAX_PARTICLES * fraction));
    if (n === this.count) return;
    this.count = n;
    if (this._field) this._seedAll();
    if (this._command) this._command.count = this.count * SEGMENTS * 2;
  }

  // ── Contract de primitivă Cesium ────────────────────────────────
  update(frameState) {
    if (!this.enabled || !this._field || this._destroyed) return;

    const now = performance.now();
    let dt = this._lastMs ? (now - this._lastMs) / 1000 : 0;
    this._lastMs = now;
    // Un tab revenit din fundal ar da un dt uriaș, care ar teleporta tot câmpul.
    dt = Math.min(dt, 0.05);
    if (dt <= 0) return;

    this._step(dt);
    this._buildVertices();

    const context = frameState.context;
    if (!this._command) this._createResources(context);

    this._posBuffer.copyFromArrayView(this._positions, 0);
    this._colBuffer.copyFromArrayView(this._colors, 0);

    frameState.commandList.push(this._command);
  }

  _createResources(context) {
    this._posBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._positions, usage: BufferUsage.STREAM_DRAW,
    });
    this._colBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._colors, usage: BufferUsage.STREAM_DRAW,
    });

    this._va = new VertexArray({
      context,
      attributes: [
        {
          index: 0, vertexBuffer: this._posBuffer,
          componentsPerAttribute: 3, componentDatatype: ComponentDatatype.FLOAT,
        },
        {
          index: 1, vertexBuffer: this._colBuffer,
          componentsPerAttribute: 4, componentDatatype: ComponentDatatype.UNSIGNED_BYTE,
          normalize: true,
        },
      ],
    });

    this._sp = ShaderProgram.fromCache({
      context,
      vertexShaderSource: VERTEX_SHADER,
      fragmentShaderSource: FRAGMENT_SHADER,
      attributeLocations: { position: 0, color: 1 },
    });

    this._rs = RenderState.fromCache({
      depthTest: { enabled: true },
      depthMask: false,               // particulele nu se ocluzionează între ele
      blending: BlendingState.ALPHA_BLEND,
    });

    this._command = new DrawCommand({
      vertexArray: this._va,
      shaderProgram: this._sp,
      renderState: this._rs,
      pass: Pass.TRANSLUCENT,
      primitiveType: PrimitiveType.LINES,
      modelMatrix: Matrix4.fromTranslation(this._center),
      count: this.count * SEGMENTS * 2,
      boundingVolume: new BoundingSphere(this._center, this._radius),
      // O singură comandă care acoperă tot domeniul: culling-ul n-ar economisi
      // nimic, dar un volum de încadrare greșit ar face particulele să dispară.
      cull: false,
      owner: this,
    });
  }

  isDestroyed() {
    return this._destroyed;
  }

  destroy() {
    this._destroyed = true;
    if (this._va) this._va.destroy();
    if (this._sp) this._sp.destroy();
    this._va = this._sp = this._command = null;
    this._posBuffer = this._colBuffer = null;
    return undefined;
  }
}
